import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Check, X, Trash2, Eye, Image, ImageOff } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDepositsPage() {
  const { t } = useLanguage();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [viewDeposit, setViewDeposit] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewImage, setViewImage] = useState(null);

  useEffect(() => {
    fetchDeposits();
  }, []);

  const fetchDeposits = async () => {
    try {
      const response = await api.getAdminDeposits();
      setDeposits(response.data);
    } catch (error) {
      console.error('Failed to fetch deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (depositId) => {
    try {
      await api.confirmDeposit(depositId, 'confirm');
      toast.success(t('admin_deposits_confirmed'));
      fetchDeposits();
    } catch (error) {
      toast.error(t('admin_deposits_confirm_failed'));
    }
  };

  const handleReject = async (depositId) => {
    if (!window.confirm(t('admin_deposits_reject_confirm_text'))) return;
    try {
      await api.confirmDeposit(depositId, 'reject');
      toast.success(t('admin_deposits_rejected'));
      fetchDeposits();
    } catch (error) {
      toast.error(t('admin_deposits_reject_failed'));
    }
  };

  const handleDelete = async (depositId) => {
    if (!window.confirm(t('admin_deposits_delete_confirm_text'))) return;
    try {
      await api.deleteDeposit(depositId);
      toast.success(t('admin_deposits_deleted'));
      fetchDeposits();
    } catch (error) {
      toast.error(t('admin_deposits_delete_failed'));
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: t('status_pending'), className: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/20' },
      confirmed: { label: t('admin_deposits_status_confirmed'), className: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/20' },
      rejected: { label: t('admin_deposits_status_rejected'), className: 'bg-red-500/15 text-red-700 border-red-500/20' },
    };
    const c = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${c.className}`}>
        {c.label}
      </span>
    );
  };

  const getMethodLabel = (method) => {
    const methods = {
      bank_transfer: t('admin_deposits_method_bank'),
      convenience_store: t('admin_deposits_method_convenience'),
      admin_manual: t('admin_deposits_method_admin'),
    };
    return methods[method] || method || t('admin_deposits_method_bank');
  };

  const filteredDeposits = deposits.filter(d => {
    if (filter === 'all') return true;
    return d.status === filter;
  });

  const pendingCount = deposits.filter(d => d.status === 'pending').length;
  const confirmedCount = deposits.filter(d => d.status === 'confirmed').length;
  const totalConfirmed = deposits.filter(d => d.status === 'confirmed').reduce((sum, d) => sum + d.amount, 0);

  return (
    <AdminLayout>
      <div data-testid="admin-deposits-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            {t('admin_nav_deposits')}
          </h1>
          <p className="text-muted-foreground">
            {t('admin_deposits_subtitle')}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-card border border-border/50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-primary font-mono">{deposits.length}</div>
            <div className="text-xs text-muted-foreground">{t('deposit')}</div>
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-yellow-700 font-mono" data-testid="pending-count">{pendingCount}</div>
            <div className="text-xs text-yellow-700">{t('status_pending')}</div>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-emerald-700 font-mono">¥{totalConfirmed.toLocaleString()}</div>
            <div className="text-xs text-emerald-700">{t('admin_deposits_status_confirmed')}</div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6" data-testid="deposit-filters">
          {[
            { key: 'all', label: `${t('all')} (${deposits.length})` },
            { key: 'pending', label: `${t('status_pending')} (${pendingCount})` },
            { key: 'confirmed', label: `${t('admin_deposits_status_confirmed')} (${confirmedCount})` },
            { key: 'rejected', label: `${t('admin_deposits_status_rejected')} (${deposits.filter(d => d.status === 'rejected').length})` },
          ].map(f => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? 'default' : 'outline'}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('name')}</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-muted-foreground">{t('amount')}</th>
                    <th className="text-center px-6 py-4 text-sm font-medium text-muted-foreground">{t('admin_deposits_receipt_col')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('admin_users_status')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('history_date')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('edit')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredDeposits.map((deposit) => (
                    <tr key={deposit.id} className="hover:bg-secondary/30 smooth-transition" data-testid={`deposit-row-${deposit.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-primary text-sm">{deposit.user_name || '-'}</div>
                        <div className="text-xs text-muted-foreground">{deposit.user_email || ''}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="font-mono font-bold text-primary">¥{deposit.amount.toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {deposit.receipt_image ? (
                          <button
                            onClick={() => setViewImage(`${API_URL}${deposit.receipt_image}`)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-500/10 border border-blue-500/20 rounded-full hover:bg-blue-500/20 transition-colors"
                            data-testid={`view-receipt-${deposit.id}`}
                          >
                            <Image className="h-3 w-3" />
                            {t('confirm')}
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/50">
                            <ImageOff className="h-3 w-3" />
                            {t('none')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(deposit.status)}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {new Date(deposit.created_at).toLocaleString('ja-JP')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => { setViewDeposit(deposit); setViewDialogOpen(true); }}
                            title={t('admin_users_details_btn')}
                            data-testid={`view-deposit-${deposit.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {deposit.status === 'pending' && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-emerald-600 hover:bg-emerald-50"
                                onClick={() => handleConfirm(deposit.id)}
                                title={t('approve')}
                                data-testid={`confirm-deposit-${deposit.id}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-600 hover:bg-red-50"
                                onClick={() => handleReject(deposit.id)}
                                title={t('reject')}
                                data-testid={`reject-deposit-${deposit.id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(deposit.id)}
                            title={t('admin_users_delete_btn')}
                          >
                            data-testid={`delete-deposit-${deposit.id}`}
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredDeposits.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground">
                        {t('admin_deposits_no_results')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* View Deposit Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('admin_deposits_detail_title')}</DialogTitle>
              <DialogDescription>{t('admin_deposits_deposit_id')}: {viewDeposit?.id?.substring(0, 8)}</DialogDescription>
            </DialogHeader>
            {viewDeposit && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('admin_deposits_user_col')}</div>
                    <div className="font-medium text-primary">{viewDeposit.user_name}</div>
                    <div className="text-xs text-muted-foreground">{viewDeposit.user_email}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('admin_deposits_amount_col')}</div>
                    <div className="text-2xl font-bold font-mono text-primary">¥{viewDeposit.amount.toLocaleString()}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('admin_deposits_method_label')}</div>
                    <div className="font-medium">{getMethodLabel(viewDeposit.method)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('admin_deposits_status_label')}</div>
                    <div>{getStatusBadge(viewDeposit.status)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('admin_deposits_request_date')}</div>
                    <div className="text-sm">{new Date(viewDeposit.created_at).toLocaleString('ja-JP')}</div>
                  </div>
                  {viewDeposit.confirmed_at && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">{t('admin_deposits_process_date')}</div>
                      <div className="text-sm">{new Date(viewDeposit.confirmed_at).toLocaleString('ja-JP')}</div>
                    </div>
                  )}
                </div>
                {viewDeposit.note && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('admin_deposits_memo')}</div>
                    <div className="text-sm bg-secondary/30 rounded-lg p-3">{viewDeposit.note}</div>
                  </div>
                )}
                {viewDeposit.notes && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('admin_deposits_user_memo')}</div>
                    <div className="text-sm bg-secondary/30 rounded-lg p-3">{viewDeposit.notes}</div>
                  </div>
                )}

                {/* Receipt Image Section */}
                <div>
                  <div className="text-xs text-muted-foreground mb-2">{t('admin_deposits_receipt')}</div>
                  {viewDeposit.receipt_image ? (
                    <div
                      className="border border-border rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => { setViewImage(`${API_URL}${viewDeposit.receipt_image}`); }}
                      data-testid="dialog-receipt-image"
                    >
                      <img
                        src={`${API_URL}${viewDeposit.receipt_image}`}
                        alt={t('admin_deposits_receipt')}
                        className="w-full max-h-64 object-contain bg-secondary/20"
                      />
                      <div className="text-xs text-center text-blue-600 py-1.5 bg-blue-500/5 border-t border-border">
                        {t('admin_deposits_click_enlarge')}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground/60 bg-secondary/20 rounded-xl p-4 justify-center">
                      <ImageOff className="h-5 w-5" />
                      {t('admin_deposits_no_receipt')}
                    </div>
                  )}
                </div>

                {viewDeposit.status === 'pending' && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      className="flex-1 gap-2"
                      onClick={() => { handleConfirm(viewDeposit.id); setViewDialogOpen(false); }}
                      data-testid="dialog-confirm-deposit"
                    >
                      <Check className="h-4 w-4" />
                      {t('approve')}
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1 gap-2"
                      onClick={() => { handleReject(viewDeposit.id); setViewDialogOpen(false); }}
                      data-testid="dialog-reject-deposit"
                    >
                      <X className="h-4 w-4" />
                      {t('reject')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Full Image Viewer Dialog */}
        <Dialog open={!!viewImage} onOpenChange={() => setViewImage(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{t('admin_deposits_receipt')}</DialogTitle>
            </DialogHeader>
            {viewImage && (
              <div className="mt-2">
                <img
                  src={viewImage}
                  alt={t('admin_deposits_receipt')}
                  className="w-full rounded-lg"
                  data-testid="fullscreen-receipt-image"
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
